import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Chip, IconButton, TableSortLabel,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputAdornment, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  BusinessCenter, Edit, Search, Block,
} from '@mui/icons-material';
import apiClient from '../api/client';
import { Client } from '../types';
import ClientFormDialog from '../components/admin/ClientFormDialog';

type SortField = 'client_name' | 'client_code' | 'primary_contact_name' | 'primary_contact_email' | 'active_employees' | 'engagement_type' | 'status';
type SortDir = 'asc' | 'desc';

export default function AdminClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'billable' | 'non_billable' | 'test'>('billable');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('client_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Client | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/clients');
      setClients(data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle ?action=add from quick actions
  useEffect(() => {
    if (searchParams.get('action') === 'add') {
      setFormOpen(true);
      setEditClient(null);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let result = [...clients];

    // Status filter
    if (statusFilter === 'billable') {
      result = result.filter(c => c.status === 'active' && !c.is_test);
    } else if (statusFilter === 'non_billable') {
      result = result.filter(c => c.status === 'inactive');
    } else if (statusFilter === 'test') {
      result = result.filter(c => c.is_test === true);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.client_name.toLowerCase().includes(q) ||
        c.client_code.toLowerCase().includes(q) ||
        (c.primary_contact_name && c.primary_contact_name.toLowerCase().includes(q))
      );
    }

    // Sort
    result.sort((a, b) => {
      const aVal = (a[sortField] ?? '').toString().toLowerCase();
      const bVal = (b[sortField] ?? '').toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [clients, search, statusFilter, sortField, sortDir]);

  const handleOpenAdd = () => {
    setEditClient(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (client: Client) => {
    setEditClient(client);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditClient(null);
  };

  const handleSaved = () => {
    setSnackbar({ message: editClient ? 'Client updated' : 'Client created', severity: 'success' });
    fetchData();
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await apiClient.delete(`/clients/${deactivateTarget.id}`);
      setSnackbar({ message: `${deactivateTarget.client_name} deactivated`, severity: 'success' });
      setDeactivateTarget(null);
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to deactivate', severity: 'error' });
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316'];
  const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getStatusChip = (c: Client) => {
    if (c.is_test) return <Chip label="Test" size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 500, fontSize: 11, height: 24 }} />;
    if (c.status === 'active') return <Chip label="Billable" size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 500, fontSize: 11, height: 24 }} />;
    return <Chip label="Non Billable" size="small" sx={{ backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 500, fontSize: 11, height: 24 }} />;
  };

  const getTypeChip = (c: Client) => {
    if (c.is_indirect) return <Chip label="Indirect" size="small" sx={{ backgroundColor: '#F3E8FF', color: '#7E22CE', fontWeight: 500, fontSize: 11, height: 24 }} />;
    return <Chip label="Direct" size="small" sx={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: 500, fontSize: 11, height: 24 }} />;
  };

  const SortHeader = ({ field, label, width, align }: { field: SortField; label: string; width?: number | string; align?: 'left' | 'center' | 'right' }) => (
    <TableCell sx={{ fontWeight: 600, width }} align={align}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Clients</Typography>
          <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.25 }}>Manage client accounts and engagement details</Typography>
        </Box>
        <Button variant="contained" startIcon={<BusinessCenter />} onClick={handleOpenAdd}
          sx={{ borderRadius: '10px', px: 2.5, py: 1 }}>
          Add Client
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search name, code, contact..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#94A3B8' }} /></InputAdornment>,
          }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => v && setStatusFilter(v)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
          <ToggleButton value="billable" sx={{ textTransform: 'none', px: 2 }}>Billable</ToggleButton>
          <ToggleButton value="non_billable" sx={{ textTransform: 'none', px: 2 }}>Non Billable</ToggleButton>
          <ToggleButton value="test" sx={{ textTransform: 'none', px: 2 }}>Test</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
            All Clients
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
            {filtered.length} client{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader field="client_name" label="Client Name" />
              <SortHeader field="primary_contact_name" label="Contact Name" width={150} />
              <SortHeader field="primary_contact_email" label="Contact Email" width={180} />
              <SortHeader field="engagement_type" label="Engagement" width={110} />
              <SortHeader field="active_employees" label="Employees" width={90} align="center" />
              <SortHeader field="status" label="Status" width={85} />
              <TableCell sx={{ fontWeight: 600, width: 85 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {clients.length === 0 ? 'No clients yet' : 'No clients match filters'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        backgroundColor: `${getAvatarColor(c.client_name)}15`,
                        color: getAvatarColor(c.client_name),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, flexShrink: 0,
                      }}>
                        {getInitials(c.client_name)}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                          {c.client_name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94A3B8', fontSize: 11 }}>
                          {c.client_code}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={13}>{c.primary_contact_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={13}>{c.primary_contact_email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={13}>{c.engagement_type || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={c.active_employees ?? 0}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 600, fontSize: 13, minWidth: 36 }}
                    />
                  </TableCell>
                  <TableCell>
                    {getStatusChip(c)}
                  </TableCell>
                  <TableCell>
                    {getTypeChip(c)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => handleOpenEdit(c)} title="Edit"
                        sx={{ color: '#94A3B8', '&:hover': { color: '#3B82F6', backgroundColor: '#EFF6FF' } }}>
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      {c.status === 'active' && (
                        <IconButton
                          size="small"
                          onClick={() => setDeactivateTarget(c)}
                          title="Deactivate"
                          sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                        >
                          <Block sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <ClientFormDialog
        open={formOpen}
        onClose={handleFormClose}
        onSaved={handleSaved}
        client={editClient}
      />

      {/* Deactivate Confirmation */}
      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Deactivate Client</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to deactivate <strong>{deactivateTarget?.client_name}</strong>?
            This client will be hidden from active lists.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeactivateTarget(null)} color="inherit" disabled={deactivating}>Cancel</Button>
          <Button onClick={handleDeactivate} color="warning" variant="contained" disabled={deactivating}>
            {deactivating ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
